import { useEffect, useRef, useState } from "react";
import { Share2, Copy, MessageCircle, X, Check, Users, MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShareToCommunitySheet } from "@/components/community/ShareToCommunity";

const API = process.env.REACT_APP_BACKEND_URL;

export function useTrackView(type, id) {
  const sent = useRef(null);
  useEffect(() => {
    if (!type || !id || sent.current === id) return;
    sent.current = id;
    const body = JSON.stringify({ target_type: type, target_id: id });
    const token = localStorage.getItem("token");
    if (navigator.sendBeacon && !token) {
      navigator.sendBeacon(`${API}/api/track`, new Blob([body], { type: "application/json" }));
    } else {
      api.post("/track", { target_type: type, target_id: id }).catch(() => {});
    }
  }, [type, id]);
}

export function ShareButton({ type, id, size = "sm", className = "", testid }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [community, setCommunity] = useState(false);
  const [p, setP] = useState(null);
  const [copied, setCopied] = useState(false);
  const load = async () => { if (p) return p; const r = await api.get(`/share/${type}/${id}`); setP(r.data); return r.data; };
  const click = async (e) => {
    e.preventDefault(); e.stopPropagation();
    try { await load(); setOpen(true); } catch { toast.error("Não foi possível preparar a partilha"); }
  };
  const native = async () => { try { await navigator.share({ title: p.title, text: p.text, url: p.og_url }); setOpen(false); } catch (err) { if (err?.name !== "AbortError") toast.error("Não foi possível partilhar"); } };
  const copy = async () => { try { await navigator.clipboard.writeText(p.og_url); setCopied(true); toast.success("Link copiado"); setTimeout(() => setCopied(false), 1500); } catch { toast.error("Não foi possível copiar"); } };
  const wa = () => window.open(`https://wa.me/?text=${encodeURIComponent(`${p.title}\n${p.og_url}`)}`, "_blank", "noreferrer");
  const toCommunity = () => { if (!user) { toast.info("Inicia sessão para partilhar numa comunidade"); return; } setOpen(false); setCommunity(true); };
  return (
    <>
      <button type="button" onClick={click} aria-label="Partilhar" data-testid={testid || `share-${type}-${id}`}
              className={`inline-flex items-center gap-1 rounded-full transition-colors text-zinc-600 hover:text-black ${size === "lg" ? "px-3 py-2 text-xs font-semibold border border-zinc-300 hover:border-black" : "p-1.5"} ${className}`}>
        <Share2 size={size === "lg" ? 16 : 14} />{size === "lg" && <span>Partilhar</span>}
      </button>
      {open && p && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={() => setOpen(false)} data-testid="share-sheet">
          <div className="bg-white w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl p-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3"><h3 className="font-display font-bold">Partilhar</h3><button onClick={() => setOpen(false)} aria-label="Fechar" data-testid="share-close" className="p-1"><X size={16} /></button></div>
            <p className="text-xs text-zinc-600 mb-3 truncate">{p.title}</p>
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="share-whatsapp" onClick={wa} className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#25D366] text-white text-xs font-semibold"><MessageCircle size={14} /> WhatsApp</button>
              <button data-testid="share-copy" onClick={copy} className="flex items-center justify-center gap-2 py-2.5 rounded-xl border border-zinc-300 text-xs font-semibold hover:border-black">{copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copiado" : "Copiar link"}</button>
              <button data-testid="share-to-community" onClick={toCommunity} className="col-span-2 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-black text-white text-xs font-semibold"><Users size={14} /> Partilhar numa comunidade</button>
              {typeof navigator !== "undefined" && navigator.share && <button data-testid="share-native" onClick={native} className="col-span-2 flex items-center justify-center gap-2 py-2 rounded-xl text-xs text-zinc-600 hover:text-black"><MoreHorizontal size={14} /> Mais opções</button>}
            </div>
          </div>
        </div>
      )}
      {community && <ShareToCommunitySheet type={type} id={id} onClose={() => setCommunity(false)} />}
    </>
  );
}
